import {
  AddInternalUserRequest,
  AddInternalUserResponse,
} from "@services/models/InternalUsers/AddInternalUserModel";
import {
  GetInternalUsersParams,
  GetInternalUsersResponse,
} from "@services/models/InternalUsers/GetInternalUsersModel";
import { RemoveInternalUserResponse } from "@services/models/InternalUsers/RemoveInternalUserModel";
import { GetMerchantListsResponse } from "@services/models/UserLists/GetMerchantListsModel";
import {
  GetUserListsResponse,
  KycStatus,
} from "@services/models/UserLists/GetUserListsModel";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";

// Existing model imports

type BinaryFlag = 0 | 1;

export interface MerchantListParams {
  search?: string;
  isDebitFrozen?: BinaryFlag;
  isActive?: BinaryFlag;
  page?: number; // default 1
  rows?: number; // default 100
}

export interface UserListParams {
  search?: string;
  isDebitFrozen?: BinaryFlag;
  kycStatus?: KycStatus; // "0" | "1" | "2" | "pending"
  page?: number; // default 1
  rows?: number; // default 100
  accountType?: string | undefined;
}

const omitUndefined = (obj: Record<string, any>) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));
class UserListsApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getMerchantsList(
    params: MerchantListParams = {}
  ): Promise<GetMerchantListsResponse> {
    const query = omitUndefined({
      search: params.search,
      isDebitFrozen: params.isDebitFrozen
        ? params.isDebitFrozen === 1
          ? true
          : false
        : undefined,
      isActive: params.isActive
        ? params.isActive === 1
          ? true
          : false
        : undefined,
      page: params.page ?? 1,
      rows: params.rows ?? 100,
    });

    try {
      const response = await this.api.get<GetMerchantListsResponse>(
        "/adminPanel/getMerchantsList",
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error("GetMerchantLists error:", error);
      throw error;
    }
  }

  public async getUsersList(
    params: UserListParams = {}
  ): Promise<GetUserListsResponse> {
    const query = omitUndefined({
      search: params.search,
      isDebitFrozen: params.isDebitFrozen
        ? params.isDebitFrozen === 1
          ? true
          : false
        : undefined,
      kycStatus: params.kycStatus,
      page: params.page ?? 1,
      rows: params.rows ?? 100,
      accountType: params.accountType || undefined,
    });

    try {
      const response = await this.api.get<GetUserListsResponse>(
        "/adminPanel/getUsersList",
        { params: query }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getInternalUsersList(
    params: GetInternalUsersParams = {}
  ): Promise<GetInternalUsersResponse> {
    const query = omitUndefined({
      limit: params.limit ?? 50,
      offset: params.offset ?? 0,
      userId: params.userId,
    });

    try {
      const response = await this.api.get<GetInternalUsersResponse>(
        "/adminPanel/internal-users/list",
        { params: query }
      );
      return response.data;
    } catch (error) {
      console.error("GetInternalUsersList error:", error);
      throw error;
    }
  }

  public async addInternalUser(
    request: AddInternalUserRequest
  ): Promise<AddInternalUserResponse> {
    try {
      const response = await this.api.post<AddInternalUserResponse>(
        "/adminPanel/internal-users/add",
        request
      );
      return response.data;
    } catch (error) {
      console.error("AddInternalUser error:", error);
      throw error;
    }
  }

  public async removeInternalUser(
    userId: number
  ): Promise<RemoveInternalUserResponse> {
    try {
      const response = await this.api.delete<RemoveInternalUserResponse>(
        `/adminPanel/internal-users/remove/${userId}`
      );
      return response.data;
    } catch (error) {
      console.error("RemoveInternalUser error:", error);
      throw error;
    }
  }
}

export const userListsApis = new UserListsApiService();
